import React, { useState, useEffect } from "react";

import {db} from '../../firebaseConfig';

import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import { getUserSystemInfo } from "../../common/Authenticator";

const APP_CONFIG = {
  brand: "Pawsyly®",
  accent: "#a64d79",

  headline:
    "I built a small tool so my pet sitting business wouldn’t take over my life.",
  subHeadline:
    "Not a startup. Just something I needed to stay organized and sane.",

  introTitle: "Running my pet sitting business felt messy all the time.",
  introProblems: [
    "Client messages scattered across texts, DMs, and emails.",
    "Trying to remember pet details, feeding times, and schedules.",
    "Second-guessing myself after bookings.",
    "Spending more time organizing than actually caring for pets.",
  ],

  realizationText:
    "For a long time I thought I was just bad at staying organized. Eventually I realized the problem was the tools I was using.",

  solutionText:
    "So I built something small for myself to keep everything in one place.",

  brandText:
    "I’ve been using it daily ever since. A few other pet sitters asked if they could use it too.",

  familiarTitle: "If you do pet sitting, this probably sounds familiar:",
  familiarProblems: [
    "Managing bookings across texts, DMs, and emails",
    "Re-checking notes so you don’t miss instructions",
    "Trying to remember details about multiple pets at once",
    "Feeling like your business never really shuts off",
  ],

  enoughText:
    "That constant mental load was the part that burned me out the most.",

  primaryCtaTitle: "If you’re curious, you can see exactly how I use it.",
  primaryCtaButton: "View the setup I use daily",
  primaryCtaSubtext:
    "This opens a read-only demo. No account. No email. Nothing to buy.",

  curiousTitle: "What the tool actually does",
  curiousText1:
    "It keeps bookings, reminders, pet details, and client notes in one place.",
  curiousText2:
    "Nothing fancy — just fewer things to keep in your head every day.",

  dailyUse: "This is what I actually use.",
  dailyUseSub:
    "It handles recurring clients, pet profiles, reminders, and daily schedules.",
};

const PaidMarketingLandingPageReddit = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {

    if (window.fbq) {
      
      window.fbq('track', 'ViewContent', {
        app: 'pawsyly'
      });

    }

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const responsiveStyles = {
    ...styles,
    twoColumn: {
      ...styles.twoColumn,
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: isMobile ? "40px" : "60px",
    },
  };

  const getSessionId = () => {
    let id = sessionStorage.getItem('ppcSessionId');
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem('ppcSessionId', id);
    }
    return id;
  };

  const handleViewDemo = async () => {
    const sessionId = getSessionId();

    const systemInfo = await getUserSystemInfo();

    await addDoc(collection(db, "appFunnelEvents"), {
      event: "view_demo",
      app: "pawsyly",
      source: "reddit",
      sessionId,
      page: "/reddit",
      timestamp: serverTimestamp(),
      systemInfo
    });

    // ✅ GA4 event
    if (window.gtag) {
      window.gtag('event', 'view_demo_click', {
        event_category: 'ppc_funnel',
        app: "pawsyly",
        source: "reddit",
        funnel_stage: "demo_intent",
      });
    }

    window.location.href = "/demo";
  };

  return (
    <div style={responsiveStyles.container}>
      <main style={responsiveStyles.main}>
        {/* Hero */}
        <section style={responsiveStyles.hero}>
          <h1 style={responsiveStyles.headline}>{APP_CONFIG.headline}</h1>
          <p style={responsiveStyles.subHeadline}>{APP_CONFIG.subHeadline}</p>
        </section>

        {/* Story */}
        <section style={responsiveStyles.storySection}>
          <h2 style={responsiveStyles.storyTitle}>{APP_CONFIG.introTitle}</h2>
          <ul style={responsiveStyles.bulletList}>
            {APP_CONFIG.introProblems.map((problem, idx) => (
              <li key={idx} style={responsiveStyles.bulletItem}>
                {problem}
              </li>
            ))}
          </ul>
          <p style={responsiveStyles.paragraph}>
            {APP_CONFIG.realizationText}
          </p>
          <p style={responsiveStyles.paragraph}>
            {APP_CONFIG.solutionText}
          </p>
          <p style={responsiveStyles.paragraph}>{APP_CONFIG.brandText}</p>
        </section>

        {/* Two Column */}
        <section style={responsiveStyles.twoColumn}>
          {/* Left */}
          <div style={responsiveStyles.leftColumn}>
            <h2 style={responsiveStyles.sectionTitle}>
              {APP_CONFIG.familiarTitle}
            </h2>
            <ul style={responsiveStyles.bulletList}>
              {APP_CONFIG.familiarProblems.map((problem, idx) => (
                <li key={idx} style={responsiveStyles.bulletItem}>
                  {problem}
                </li>
              ))}
            </ul>

            <p style={responsiveStyles.paragraph}>{APP_CONFIG.enoughText}</p>

            <div style={responsiveStyles.imageSection}>
              <img
                src="/images/pet-sitting-demo.png"
                alt="Pet profiles and reminders"
                style={responsiveStyles.demoImage}
              />
              <p style={responsiveStyles.imageCaption}>
                <strong>{APP_CONFIG.dailyUse}</strong>{" "}
                {APP_CONFIG.dailyUseSub}
              </p>
            </div>
          </div>

          {/* Right */}
          <div style={responsiveStyles.rightColumn}>
            {/* CTA Box */}
            <div style={responsiveStyles.formBox}>
              <h3 style={responsiveStyles.formTitle}>
                {APP_CONFIG.primaryCtaTitle}
              </h3>

              <button
                style={responsiveStyles.button}
                onClick={handleViewDemo}
              >
                {APP_CONFIG.primaryCtaButton}
              </button>

              <p style={responsiveStyles.formSubtext}>
                {APP_CONFIG.primaryCtaSubtext}
              </p>
            </div>

            {/* Curious */}
            <div style={responsiveStyles.curiousBox}>
              <h3 style={responsiveStyles.curiousTitle}>
                {APP_CONFIG.curiousTitle}
              </h3>
              <p style={responsiveStyles.curiousText}>
                {APP_CONFIG.curiousText1}
              </p>
              <p style={responsiveStyles.curiousText}>
                {APP_CONFIG.curiousText2}
              </p>

              <p style={{ fontSize: "15px", color: "#555", marginTop: "20px" }}>
                <strong>This probably isn’t for you if:</strong>
                <br />
                You only pet sit occasionally, or you’re happy juggling
                everything in notes and messages.
              </p>
            </div>
          </div>
        </section>
      </main>
      <div>

                                    <center>
                      <table>
                        <tr>
                          <td>
                            <a href="privacy.html" target="_blank">
                                               <p>Privacy Policy</p>
                                            </a>
                          </td>
                          <td>
                            &nbsp;&nbsp;&nbsp;
                          </td>
                          <td>
                                 <a href="terms-of-service.html"  target="_blank">
                                                <p>Terms and Conditions</p>
                                  </a>
                          </td>
                          <td>
                            &nbsp;&nbsp;&nbsp;
                          </td>
                   
                          </tr>
                          </table>
                          <table>
                          <tr colspan="3">
                            <td style={{textAlign:'center'}}>
                              <p>&#169; 2026 Copyright Pawsyly<sup style={{fontSize:'12px'}}>&#174;</sup></p>
                            </td>
                          </tr>
                        
                      </table>
                    </center>               
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#f6f6f6",
    minHeight: "100vh",
    fontFamily:
      '"Source Serif 4", Georgia, "Times New Roman", serif',  //-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif
  },
  main: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "60px 40px",
  },
  hero: { marginBottom: "60px" },
  headline: {
    fontSize: "clamp(32px, 5vw, 48px)",
    fontWeight: "700",
    lineHeight: "1.15",
    color: "#1a1a1a",
  },
  subHeadline: {
    fontSize: "clamp(16px, 2.5vw, 20px)",
    lineHeight: "1.5",
    color: "#333",
  },
  storySection: { marginBottom: "80px" },
  storyTitle: {
    fontSize: "clamp(24px, 3vw, 32px)",
    fontWeight: "700",
    marginBottom: "20px",
  },
  bulletList: { paddingLeft: "20px", marginBottom: "24px" },
  bulletItem: { fontSize: "18px", marginBottom: "8px" },
  paragraph: { fontSize: "18px", marginBottom: "16px" },
  twoColumn: { display: "grid" },
  leftColumn: { display: "flex", flexDirection: "column" },
  rightColumn: { display: "flex", flexDirection: "column", gap: "24px" },
  sectionTitle: { fontSize: "28px", fontWeight: "700", marginBottom: "24px" },
  imageSection: { marginTop: "auto" },
  demoImage: { width: "100%", maxWidth: "440px", marginBottom: "12px" },
  imageCaption: { fontSize: "15px" },
  formBox: {
    backgroundColor: "#e8ddd3",
    borderRadius: "8px",
    padding: "32px",
  },
  formTitle: { fontSize: "24px", fontWeight: "700", marginBottom: "24px" },
  button: {
    padding: "16px 24px",
    fontSize: "16px",
    fontWeight: "600",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#d85a2f",
    color: "#fff",
    cursor: "pointer",
  },
  formSubtext: { fontSize: "14px", color: "#555", marginTop: "12px" },
  curiousBox: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "32px",
  },
  curiousTitle: { fontSize: "20px", marginBottom: "16px" },
  curiousText: { fontSize: "16px", marginBottom: "12px" },
};

export default PaidMarketingLandingPageReddit;
